import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, KeyRound, Lock, Eye, EyeOff, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { LOGO_RAIZES_WHITE } from '../lib/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecuperarSenha = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipoParam = searchParams.get('tipo') || 'pais';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [userType, setUserType] = useState(tipoParam === 'admin' ? 'admin' : 'pais');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [userName, setUserName] = useState('');

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) { toast.error('Informe seu e-mail ou celular'); return; }
    setLoading(true);
    try {
      const body = { user_type: userType };
      if (identifier.includes('@')) {
        body.email = identifier.trim();
      } else {
        body.celular = identifier.trim();
      }
      const res = await axios.post(`${API}/recovery/request`, body);
      setUserName(res.data.nome || '');
      setMaskedEmail(res.data.masked_email || '');
      toast.success('Codigo enviado para seu e-mail!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao solicitar recuperacao');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error('Informe o codigo'); return; }
    if (newPassword.length < 4) { toast.error('A senha deve ter pelo menos 4 caracteres'); return; }
    if (newPassword !== confirmPassword) { toast.error('As senhas nao coincidem'); return; }
    setLoading(true);
    try {
      const body = { code: code.trim(), new_password: newPassword, user_type: userType };
      if (identifier.includes('@')) {
        body.email = identifier.trim();
      } else {
        body.celular = identifier.trim();
      }
      await axios.post(`${API}/recovery/reset`, body);
      toast.success('Senha redefinida com sucesso!');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally { setLoading(false); }
  };

  const loginPath = userType === 'pais' ? '/app-pais/login' : '/login';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A4D2E] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-8">
          <img src={LOGO_RAIZES_WHITE} alt="Ministério Raízes" className="h-16 mx-auto mb-4 drop-shadow-xl" />
          <h1 className="font-nunito font-bold text-2xl text-white mb-1">Recuperar Senha</h1>
          <p className="font-inter text-white/60 text-sm">
            {step === 1 && 'Informe seu e-mail ou celular cadastrado'}
            {step === 2 && 'Verifique seu e-mail'}
            {step === 3 && 'Senha redefinida!'}
          </p>
        </div>

        {/* Step 1: Request */}
        {step === 1 && (
          <Card className="bg-white rounded-2xl p-8 shadow-2xl">
            <form onSubmit={handleRequest} className="space-y-5">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Tipo de Conta</Label>
                <div className="flex gap-3">
                  <button type="button" data-testid="type-pais"
                    onClick={() => setUserType('pais')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border ${userType === 'pais' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1A4D2E]'}`}>
                    Pais/Responsavel
                  </button>
                  <button type="button" data-testid="type-admin"
                    onClick={() => setUserType('admin')}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border ${userType === 'admin' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1A4D2E]'}`}>
                    Voluntario/Admin
                  </button>
                </div>
              </div>

              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">E-mail ou Celular</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input data-testid="input-identifier" type="text" value={identifier} onChange={e => setIdentifier(e.target.value)}
                    placeholder="seu@email.com ou (00) 00000-0000" className="pl-11 rounded-xl" required />
                </div>
              </div>

              <Button type="submit" data-testid="btn-request" disabled={loading}
                className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold">
                {loading ? 'Enviando...' : 'Enviar Codigo por E-mail'}
              </Button>
            </form>
            <div className="text-center mt-4">
              <button onClick={() => navigate(loginPath)} className="font-inter text-sm text-gray-500 underline">
                Voltar ao login
              </button>
            </div>
          </Card>
        )}

        {/* Step 2: Enter Code */}
        {step === 2 && (
          <Card className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="bg-[#F2F7F4] rounded-xl p-4 mb-5 text-center">
              <Send className="w-8 h-8 text-[#1A4D2E] mx-auto mb-2" />
              <p className="font-inter text-sm text-[#1A4D2E] font-semibold mb-1">
                E-mail enviado para {maskedEmail}
              </p>
              <p className="font-inter text-xs text-gray-500">
                Verifique sua caixa de entrada (e spam). O codigo expira em 15 minutos.
              </p>
            </div>

            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Codigo de Recuperacao</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input data-testid="input-code" value={code} onChange={e => setCode(e.target.value)}
                    placeholder="000000" maxLength={6} className="pl-11 rounded-xl text-center text-2xl tracking-[0.5em] font-mono" required />
                </div>
              </div>

              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input data-testid="input-new-password" type={showPassword ? 'text' : 'password'}
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Minimo 4 caracteres" className="pl-11 pr-10 rounded-xl" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-new-password">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input data-testid="input-confirm-password" type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repita a senha" className="pl-11 pr-10 rounded-xl" required />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="toggle-confirm-password">
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" data-testid="btn-reset" disabled={loading}
                className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold">
                {loading ? 'Redefinindo...' : 'Redefinir Senha'}
              </Button>
            </form>

            <div className="text-center mt-4">
              <button onClick={() => setStep(1)} className="font-inter text-sm text-gray-500 underline">
                Solicitar novo codigo
              </button>
            </div>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card className="bg-white rounded-2xl p-8 shadow-2xl text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="font-nunito font-bold text-xl text-[#1A4D2E] mb-2">Senha Redefinida!</h2>
            <p className="font-inter text-sm text-gray-500 mb-6">Sua senha foi alterada com sucesso. Faca login com sua nova senha.</p>
            <Button data-testid="btn-go-login" onClick={() => navigate(loginPath)}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold">
              Ir para o Login
            </Button>
          </Card>
        )}

        <div className="text-center mt-6">
          <button onClick={() => navigate('/')} className="font-inter text-white/50 hover:text-white/80 text-sm underline">
            Voltar ao inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecuperarSenha;
